import { ArrowRightIcon, PlusIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { useState } from 'react';
import styled from 'styled-components';

import EventNameSticker from 'dashboard/components/EventNameSticker';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import Permissioned from 'dashboard/components/Permissioned';
import Tooltip from 'dashboard/components/Tooltip';
import WebhookStatusSticker from 'dashboard/components/WebhookStatusSticker';
import DismissibleBanner from 'dashboard/components-lib/DismissibleBanner/DismissibleBanner';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import {
  FormCreateWebhook,
  FormCreateWebhookData,
} from 'dashboard/forms/FormCreateWebhook/FormCreateWebhook';
import {
  FormPasswordConfirmationModal,
  FormPasswordConfirmationModalData,
} from 'dashboard/forms/FormPasswordConfirmationModal';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import TableLayout, {
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { Organization } from 'dashboard/models/organization';
import { Webhook } from 'dashboard/models/webhook';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { Paragraph } from 'design-system/components/Text';
import AutomationNullState from 'images/dashboard/automation-null-state.png';
import { LocalStorageKey } from 'lib/clients/StorageClient';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

const EventsTooltip = styled('div')`
  max-height: 350px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const NullImage = styled('img')`
  max-width: 350px;
`;

interface DashboardWebhooksViewProps {
  organization: Organization;
  webhooks: Webhook[];
  isLoadingWebhooks: boolean;
  handleCreateWebhook: (
    data: FormCreateWebhookData,
    meta?: FormPasswordConfirmationModalData,
  ) => Promise<void>;
}

const DashboardWebhooksView = ({
  organization,
  webhooks,
  isLoadingWebhooks,
  handleCreateWebhook,
}: DashboardWebhooksViewProps) => {
  // Do not require password confirmation if Organization has SAML enabled
  const requirePasswordConfirmation = !organization.attributes['has-saml-enabled'];

  const [
    passwordConfirmationCallback,
    // NOTE: Because this is a function state, need to "wrap" the function call
    // i.e. () => () => {}
    //
    // Reference: https://stackoverflow.com/questions/55621212/is-it-possible-to-react-usestate-in-react
    setPasswordConfirmationCallback,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  ] = useState<(meta: FormPasswordConfirmationModalData) => void>(null);

  const [
    isOpenPasswordConfirmationModal,
    { enable: showPasswordConfirmationModal, disable: hidePasswordConfirmationModal },
  ] = useToggle(false);

  const [
    isOpenCreateWebhookModal,
    { enable: showCreateWebhookModal, disable: hideCreateWebhookModal },
  ] = useToggle(false);

  return (
    <>
      <InactiveEnvironmentBanner />
      <Container>
        <Header title='Webhooks' data-test='webhooks__header'>
          <Permissioned permission={Permission.WebhookWrite}>
            <Button
              data-test='webhooks__button--create-webhook'
              width='auto'
              onClick={showCreateWebhookModal}
              prefixIcon={<PlusIcon />}
            >
              Create webhook
            </Button>
          </Permissioned>
        </Header>
        <Section>
          <Card>
            <Card.Body>
              <DismissibleBanner localStorageKey={LocalStorageKey.WebhooksInfoBanner}>
                <Paragraph>
                  <span>
                    Webhooks can notify your service about events that happen on Persona resources,
                    so your system can react accordingly. For example, you can allow a user access
                    to your product once you receive a webhook event for an approved Inquiry,
                    signifying that they have completed the onboarding flow.{' '}
                  </span>
                  <a
                    href='https://docs.withpersona.com/docs/webhooks'
                    target='_blank'
                    rel='noreferrer'
                  >
                    Read more about webhooks <ArrowRightIcon size='14' />
                  </a>
                </Paragraph>
              </DismissibleBanner>
              <TableLayout
                dataTest='webhooks__table'
                isLoading={isLoadingWebhooks}
                showHeader={true}
                isEmpty={webhooks.length === 0}
                nullState={
                  isLoadingWebhooks ? (
                    <LoadingIndicator />
                  ) : (
                    <TableNullState>
                      <NullImage src={AutomationNullState} />
                      <div>
                        Create and configure your first webhook to automate your Persona
                        integration.
                      </div>
                      <a
                        href='https://docs.withpersona.com/docs/webhooks'
                        target='_blank'
                        rel='noreferrer'
                      >
                        Learn more here.
                      </a>
                    </TableNullState>
                  )
                }
                headerTR={
                  <>
                    <TableTH>URL</TableTH>
                    <TableTH>Status</TableTH>
                    <TableTH>Events</TableTH>
                    <TableTH>{`Created at (${DateTime.local().toFormat(
                      TIMEZONE_NAME_ABBREVIATED,
                    )})`}</TableTH>
                  </>
                }
              >
                {webhooks.map((webhook) => (
                  <TableLinkTR
                    to={`/dashboard/webhooks/${webhook.id}`}
                    key={webhook.id}
                    data-test={`webhooks__table__row--${webhook.id}`}
                  >
                    <TableTD>{webhook.attributes['url']}</TableTD>
                    <TableTD>
                      <WebhookStatusSticker webhook={webhook} />
                    </TableTD>
                    <TableTD data-test={`webhooks__table__row__events--${webhook.id}`}>
                      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                      {webhook.attributes['enabled-events'].length > 1 ? (
                        // if length > 1
                        <Tooltip
                          placement='bottom-end'
                          tooltip={
                            <EventsTooltip>
                              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                              {webhook.attributes['enabled-events'].map((eventName) => (
                                <EventNameSticker eventName={eventName} key={eventName} />
                              ))}
                            </EventsTooltip>
                          }
                        >
                          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                          {webhook.attributes['enabled-events'].length} Events
                        </Tooltip>
                      ) : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      webhook.attributes['enabled-events'].length === 1 ? (
                        // else if length == 1
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        <EventNameSticker eventName={webhook.attributes['enabled-events'][0]} />
                      ) : (
                        // else length == 0
                        'No events'
                      )}
                    </TableTD>
                    <TableTD>
                      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                      {DateTime.fromISO(webhook.attributes['created-at']).toFormat(
                        DATE_AND_TIME_FORMAT,
                      )}
                    </TableTD>
                  </TableLinkTR>
                ))}
              </TableLayout>
            </Card.Body>
          </Card>
        </Section>

        <FormCreateWebhook
          isOpen={isOpenCreateWebhookModal}
          handleSubmit={(data: FormCreateWebhookData) => {
            if (requirePasswordConfirmation) {
              setPasswordConfirmationCallback(() => (meta: FormPasswordConfirmationModalData) => {
                handleCreateWebhook(data, meta);
              });
              showPasswordConfirmationModal();
            } else {
              handleCreateWebhook(data);
            }
          }}
          handleCloseModal={hideCreateWebhookModal}
        />

        <FormPasswordConfirmationModal
          isOpen={isOpenPasswordConfirmationModal}
          handleSubmit={passwordConfirmationCallback}
          handleCloseModal={hidePasswordConfirmationModal}
        />
      </Container>
    </>
  );
};

export default DashboardWebhooksView;
